import React, { useEffect, useState } from 'react';
import '../styles.scss';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'src/store/reducers/state';
import Modal from '../components/Modal';
import { useUserActions } from 'src/store/users/actions';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLoginActions } from 'src/store/login/actions';
import { errorMsg, getItem, successMsg } from 'src/components/utilities/utils';
import ToastMessage from 'src/components/ToastContainer';
import { UserInfoModel, UsersModel } from 'src/models/usersModel';

type locationState = {
  userId: string;
};
const UserDetails: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userAction = useUserActions(dispatch);
  const location = useLocation();
  const { id } = useParams<string>();
  const loginAction = useLoginActions(dispatch);
  const { userId } = (location.state as locationState) || {};
  const { userDetails, roles } = useSelector((state: RootState) => {
    return {
      userDetails: state.users.userDetails,
      roles: state.users.roles
    };
  });
  const { forgetRestPassword, success, resetPasswordObj, isMfaUpdated } = useSelector((state: RootState) => {
    return {
      forgetRestPassword: state.users.userDetails,
      success: state.login.isResetSuccess,
      resetPasswordObj: state.login.resetPasswordObj,
      isMfaUpdated: state.users.isMfaUpdated
    };
  });

  const [userInfoDetails, setUserInfoDetails] = useState<UserInfoModel | null>(null);
  const [value, setValue] = useState(userDetails?.active);

  const handleRemoveMfaClick = (UserId: string, role: string) => {
    const data = {
      userId: UserId,
      isMfaSetupEnabled: false,
      userRole: role
    };
    userAction.UserMfaSetupRequest(data);
  };

  const handleEditClick = (id: string) => {
    userAction.getUserDetailsRequest(id);
  };

  useEffect(() => {
    if (id) {
      userAction.getUserDetailsRequest(id);
    }
  }, [userDetails?.userId, isMfaUpdated]);

  useEffect(() => {
    setValue(!userDetails?.active);
  }, [userDetails?.active]);

  useEffect(() => {
    setUserInfoDetails(JSON.parse(getItem('userData')));
  }, []);

  useEffect(() => {
    if (Object.keys(resetPasswordObj).length > 0) {
      if (success) {
        successMsg('Link sent successfully.');
        loginAction.reset();
      } else {
        errorMsg('Link not sent.');
        loginAction.reset();
      }
    }
  }, [resetPasswordObj, success]);

  const handleForgetPasswordLink = (data: string) => {
    loginAction.forgetRestPasswordRequest(data);
  };

  const handleResetPassword = (data: string) => {
    navigate(`/change-password/${data}`);
  };

  const changeActiveStatus = (e: any) => {
    const userData = userDetails ?? ({} as UsersModel);
    const role = roles.filter((x) => x.name == userData.role);
    userAction.UpdateUserRequest({ ...userData, active: e, role: role[0].id });
  };

  useEffect(() => {
    if (isMfaUpdated) {
      successMsg('success');
      userAction.resetMFAStateRequest()
    }
  }, [isMfaUpdated])

  return (
    <>
      <ToastMessage />
      <div className="member-details mx-auto p-6">
        {userDetails && (
          <div className="bg-white rounded-lg shadow-md p-8">
            {/* Header */}
            <div className="flex justify-between items-start mb-8">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Details</h1>
              </div>
              <div className="flex gap-3">
                {userInfoDetails &&
                  (userInfoDetails.role === 'Admin' || userInfoDetails.role === 'Super Admin') && (
                    <button
                      className="btn w-auto py-2 px-3 mt-2 ml-0 mr-3 border rounded-lg reset-password-btn"
                      onClick={() => handleResetPassword(userDetails.userId)}>
                      Reset Password
                    </button>
                  )}
                <button
                  className="btn w-auto py-2 px-3 mt-2 ml-0 mr-3 border rounded-lg reset-password-btn"
                  onClick={() => handleForgetPasswordLink(userDetails.email)}>
                  Send Password Reset
                </button>
              </div>
            </div>

            {/* User Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-6 mb-8">
              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">Username</label>
                <span className="text-base text-gray-900">{userDetails.userName}</span>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">Status</label>
                <span
                  className={`text-base ${userDetails.active ? 'text-green-600' : 'text-red-600'}`}>
                  {userDetails.active ? 'active' : 'inactive'}
                </span>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">Name</label>
                <span className="text-base text-gray-900">{userDetails.name}</span>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">Role</label>
                <span className="text-base text-gray-900">{userDetails.role}</span>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">Email</label>
                <span className="text-base text-gray-900">{userDetails.email}</span>
              </div>

              {userDetails.phoneNumber && (
                <div>
                  <label className="text-sm font-medium text-gray-500 block mb-1">Phone</label>
                  <span className="text-base text-gray-900">{userDetails.phoneNumber}</span>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">MFA Setup</label>
                <span className="text-base text-gray-900">
                  {userDetails.isMfaSetupEnabled ? 'Enabled' : 'Not Setup'}
                </span>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500 block mb-1">Active</label>
                <input
                  type="checkbox"
                  checked={userDetails.active ? true : false}
                  readOnly
                  className="mt-1"
                />
              </div>
              {
                userDetails.isMfaSetupEnabled && (
                  <div>
                    <label className="text-sm font-medium text-gray-500 block mb-1">Remove MFA</label>
                    <button
                      type="button"
                      onClick={() => handleRemoveMfaClick(userDetails.userId, userDetails.role)}
                      className="btn-primary w-auto px-5 ml-0 mr-3 borderonly-btn"
                    >
                      Reset
                    </button>
                  </div>
                )
              }

            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => handleEditClick(userDetails.userId)}
                className="btn-primary w-auto px-5 ml-0 mr-3 borderonly-btn"
                data-bs-toggle="modal"
                data-bs-target="#editMemberModal">
                Edit
              </button>
              <button
                type="submit"
                className="btn-primary w-auto mx-0 suspend-btn"
                onClick={() => changeActiveStatus(value)}>
                {userDetails.active ? 'Deactive' : 'Activate'}
              </button>
            </div>

          </div>
        )}

        {/* Edit Modal */}
        <Modal userDetails={userDetails} modalFlag="edit" />
      </div>
    </>
  );
};

export default UserDetails;
